// Music theory utility functions
// Import from consolidated music theory module
import {
  MODES,
  MODE_NAMES_BY_SCALE as GENERATED_MODE_NAMES_BY_SCALE,
  SCALE_TYPES as GENERATED_SCALE_TYPES,
  MODE_NAMES as GENERATED_MODE_NAMES,
  MOOD_TO_SCALE as GENERATED_MOOD_TO_SCALE,
  ALL_MOODS as GENERATED_ALL_MOODS,
  getScaleTypeForMode,
  getModesForScaleType,
  getAvailableScaleTypes,
  getModeInfo,
  getMoodsForScale,
  runValidationInDev
} from './musicTheory/index';
import { ARPEGGIO_TYPES, getAllArpeggioTypes, getArpeggioTypeById, groupArpeggiosByCategory } from './chordTypes';
import { isHarshChordFromNotes } from './harshness';
import { getProgressionByName, degreeToIndex } from './chordProgressions';

// Run validation in development mode
runValidationInDev();

// All 12 notes in the chromatic scale
export const ALL_NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// Enharmonic equivalents with flats
export const ENHARMONIC_EQUIVALENTS = {
  'C#': 'Db',
  'D#': 'Eb',
  'F#': 'Gb',
  'G#': 'Ab',
  'A#': 'Bb'
};


// Keys that typically use flats
export const FLAT_KEYS = ['F', 'Bb', 'Eb', 'Ab', 'Db', 'Gb'];

// Complete enharmonic system with all possible spellings
export const ENHARMONIC_NOTES = {
  'C': ['C', 'B#', 'Dbb'],
  'C#': ['C#', 'Db', 'B##'],
  'D': ['D', 'C##', 'Ebb'],
  'D#': ['D#', 'Eb', 'Fbb'],
  'E': ['E', 'D##', 'Fb'],
  'F': ['F', 'E#', 'Gbb'],
  'F#': ['F#', 'Gb', 'E##'],
  'G': ['G', 'F##', 'Abb'],
  'G#': ['G#', 'Ab'],
  'A': ['A', 'G##', 'Bbb'],
  'A#': ['A#', 'Bb', 'Cbb'],
  'B': ['B', 'A##', 'Cb']
};

// Resolve any note name (including double sharps/flats) to an index in ALL_NOTES
export const resolveAllNotesIndex = (note) => {
  if (!note || typeof note !== 'string') return -1;
  // Direct match
  let idx = ALL_NOTES.indexOf(note);
  if (idx !== -1) return idx;
  // Single-flat mapping (Db -> C#)
  if (note.includes('b')) {
    for (const [sharp, flat] of Object.entries(ENHARMONIC_EQUIVALENTS)) {
      if (flat === note) {
        idx = ALL_NOTES.indexOf(sharp);
        if (idx !== -1) return idx;
      }
    }
  }
  // General enharmonic search (handles Bbb, E##, etc.)
  for (const [basic, equivalents] of Object.entries(ENHARMONIC_NOTES)) {
    if (Array.isArray(equivalents) && equivalents.includes(note)) {
      return ALL_NOTES.indexOf(basic);
    }
  }
  return -1;
};

// Normalize any note name to its canonical ALL_NOTES spelling
export const normalizeToAllNotes = (note) => {
  const idx = resolveAllNotesIndex(note);
  return idx === -1 ? note : ALL_NOTES[idx];
};

// Helper functions for note index operations
export const noteIndex = (note) => resolveAllNotesIndex(note);

export const scaleIndexSet = (scaleNotes) => new Set(scaleNotes.map(resolveAllNotesIndex));

export const getNoteIndexAtFret = (openNote, fret) => {
  const openIdx = ALL_NOTES.indexOf(openNote);
  return openIdx === -1 ? -1 : (openIdx + fret) % 12;
};

/**
 * Determines the proper enharmonic spelling of a note based on musical context
 * @param {string} note - The note to get enharmonic spelling for (e.g., 'C#')
 * @param {string} rootNote - The root note of the current key (e.g., 'F')
 * @param {string} mode - The current mode (e.g., 'Dorian')
 * @returns {string} - The properly spelled note name
 */
export const getEnharmonicNoteName = (note, rootNote, mode) => {
  // If the note doesn't have an enharmonic equivalent, return it as is
  if (!ENHARMONIC_EQUIVALENTS[note]) {
    return note;
  }
  
  // Sharp keys should always use sharps, not flats
  if (rootNote && rootNote.includes('#')) {
    return note; // Keep the sharp spelling
  }
  
  // Determine if we should use flats based on the key and mode
  const modeUsesFlats = typeof mode === 'string' ? 
    (MODES[mode] && MODES[mode].useFlats) : 
    false;
  const useFlats = FLAT_KEYS.includes(rootNote) || modeUsesFlats;
  
  // Return the appropriate spelling
  return useFlats ? ENHARMONIC_EQUIVALENTS[note] : note;
};

/**
 * Get the proper display name for a note based on the current scale
 * This ensures notes are displayed with the correct enharmonic spelling
 * @param {string} note - The note to display (e.g., 'C#')
 * @param {object} activeScale - The active scale object with notes array
 * @returns {string} - The properly spelled note name for display
 */
export const getProperNoteDisplay = (note, activeScale) => {
  if (!activeScale || !activeScale.notes) {
    return note;
  }
  
  // Check if this exact note is in the scale
  if (activeScale.notes.includes(note)) {
    return note;
  }
  
  // Special check: if we're looking for enharmonic equivalents in the scale
  // We need to check all possible enharmonic spellings
  for (const scaleNote of activeScale.notes) {
    // Check if scaleNote is an enharmonic equivalent of our note
    const scaleNoteOptions = ENHARMONIC_NOTES[scaleNote];
    if (scaleNoteOptions && scaleNoteOptions.includes(note)) {
      // The scale has an enharmonic equivalent of our note
      return scaleNote;
    }
  }
  
  // Also check if our note has enharmonic options that are in the scale
  const enharmonicOptions = ENHARMONIC_NOTES[note];
  if (enharmonicOptions) {
    for (const option of enharmonicOptions) {
      if (activeScale.notes.includes(option)) {
        return option;
      }
    }
  }
  
  // If not in scale, still respect the key signature
  // Sharp keys should show all notes as sharps, flat keys as flats
  if (activeScale.scaleRoot && activeScale.scaleRoot.includes('#')) {
    // Sharp key - keep sharps
    return note;
  } else if (activeScale.scaleRoot && (FLAT_KEYS.includes(activeScale.scaleRoot) || activeScale.scaleRoot.includes('b'))) {
    // Flat key - convert to flats if possible
    return ENHARMONIC_EQUIVALENTS[note] || note;
  }
  
  // Otherwise use the standard enharmonic rules
  return getEnharmonicNoteName(note, activeScale.scaleRoot, activeScale.modeName);
};

const LETTER_ORDER = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const MAJOR_TEMPLATE = [0, 2, 4, 5, 7, 9, 11];
const ACCIDENTAL_BY_OFFSET = { '-2': 'bb', '-1': 'b', 0: '', 1: '#', 2: '##' };
const CHROMATIC_DEGREE_LABELS = ['1', 'b2', '2', 'b3', '3', '4', 'b5', '5', 'b6', '6', 'b7', '7'];

// Etiquette de degre avec alteration (1, b3, #4, b7...) d'une note par rapport
// a la premiere note de la gamme affichee, celle que le mode Vue "degrees"
// compte comme 1. Une note de la gamme prend le chiffre de sa lettre (distance
// de lettre depuis la premiere note) et l'alteration qui la separe de la gamme
// majeure : la pentatonique mineure donne 1 b3 4 5 b7, le lydien #4, le
// phrygien b2, sans table par mode. Une note hors gamme prend l'intervalle
// chromatique ecrit en bemols (b2, b3, b5, b6, b7), lisible quelle que soit
// l'orthographe du nom de note.
export const getIntervalDegreeLabel = (note, activeScale) => {
  if (!activeScale || !Array.isArray(activeScale.notes) || activeScale.notes.length === 0) return note;
  const refNote = activeScale.notes[0];
  const refPc = resolveAllNotesIndex(refNote);
  const notePc = resolveAllNotesIndex(note);
  if (refPc === -1 || notePc === -1) return note;
  const semitones = (notePc - refPc + 12) % 12;
  const inScale = activeScale.notes.some((n) => resolveAllNotesIndex(n) === notePc);
  if (inScale) {
    const displayNote = getProperNoteDisplay(note, activeScale);
    const letterIndex = LETTER_ORDER.indexOf(displayNote[0]);
    const refLetterIndex = LETTER_ORDER.indexOf(refNote[0]);
    if (letterIndex !== -1 && refLetterIndex !== -1) {
      const letterDistance = (letterIndex - refLetterIndex + 7) % 7;
      let offset = semitones - MAJOR_TEMPLATE[letterDistance];
      if (offset > 6) offset -= 12;
      if (offset < -5) offset += 12;
      const accidental = ACCIDENTAL_BY_OFFSET[offset];
      if (accidental !== undefined) return `${accidental}${letterDistance + 1}`;
    }
  }
  return CHROMATIC_DEGREE_LABELS[semitones];
};

/**
 * Get the major key for a given mode
 * @param {string} rootNote - The root note of the mode (e.g., 'D')
 * @param {string} modeName - The mode name (e.g., 'Dorian')
 * @returns {string} - The major key (e.g., 'C' for D Dorian)
 */
export const getMajorKeyForMode = (rootNote, modeName) => {
  // For Major scale modes, calculate the major key root
  const majorModes = getModesForScaleType('Major');
  const modeIndex = majorModes.indexOf(modeName);
  
  if (modeIndex === -1) {
    // If it's not a major scale mode, return the root note itself
    // This handles cases like melodic minor, harmonic minor, etc.
    return rootNote;
  }
  
  // Calculate the major key root by going backwards the number of semitones
  const rootIndex = resolveAllNotesIndex(rootNote);
  if (rootIndex === -1) {
    return rootNote;
  }
  
  // Mode intervals from Ionian: 0, 2, 4, 5, 7, 9, 11 semitones
  // So if we're in Dorian (index 1), we need to go back 2 semitones to find the major key
  const modeIntervals = [0, 2, 4, 5, 7, 9, 11];
  const intervalFromMajor = modeIntervals[modeIndex];
  
  // Calculate the major key root
  const majorKeyIndex = (rootIndex - intervalFromMajor + 12) % 12;
  return ALL_NOTES[majorKeyIndex];
};

/**
 * Get proper scale spelling following the seven-letter rule
 * Each scale must use each letter name (A-G) exactly once
 * @param {string} rootNote - The root note of the scale
 * @param {number[]} scaleIntervals - Array of semitone intervals from the root
 * @returns {string[]} - Array of properly spelled note names
 */
// Spell a scale from one specific root spelling, applying the seven-letter rule
const spellScaleFromRoot = (rootNote, scaleIntervals) => {
  // Determine if we should prefer sharps or flats based on the root note
  const preferFlats = FLAT_KEYS.includes(rootNote) || rootNote.includes('b');
  const preferSharps = rootNote.includes('#');

  // Get the starting letter
  const rootLetter = rootNote[0];
  const letterSequence = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
  const startIndex = letterSequence.indexOf(rootLetter);

  // Create expected letter sequence for this scale
  const expectedLetters = [];
  for (let i = 0; i < scaleIntervals.length; i++) {
    expectedLetters.push(letterSequence[(startIndex + i) % 7]);
  }

  // Build scale with proper enharmonic spellings
  const properScale = [];

  // Resolve root index with full enharmonic support
  let rootIndex = resolveAllNotesIndex(rootNote);

  if (rootIndex === -1) {
    console.error(`Root note ${rootNote} not found`);
    return [];
  }

  scaleIntervals.forEach((interval, index) => {
    const noteIndex = (rootIndex + interval) % 12;
    const basicNote = ALL_NOTES[noteIndex];
    const expectedLetter = expectedLetters[index];

    // Find the enharmonic spelling that uses the expected letter
    const enharmonicOptions = ENHARMONIC_NOTES[basicNote];
    let properSpelling = null;

    // Try to find a spelling that matches the expected letter
    if (enharmonicOptions) {
      properSpelling = enharmonicOptions.find(note => note[0] === expectedLetter);

      // If we found multiple options with the same letter, prefer based on root
      if (!properSpelling) {
        properSpelling = basicNote;
      } else if (preferSharps && properSpelling.includes('b')) {
        // If root has sharps, avoid flats if possible
        const sharpOption = enharmonicOptions.find(
          note => note[0] === expectedLetter && !note.includes('b')
        );
        if (sharpOption) properSpelling = sharpOption;
      } else if (preferFlats && properSpelling.includes('#')) {
        // If root has flats, avoid sharps if possible
        const flatOption = enharmonicOptions.find(
          note => note[0] === expectedLetter && !note.includes('#')
        );
        if (flatOption) properSpelling = flatOption;
      }
    } else {
      properSpelling = basicNote;
    }

    properScale.push(properSpelling);
  });

  return properScale;
};

// Count double accidentals (## or bb) in a spelled scale
const countDoubleAccidentals = (notes) =>
  notes.filter(n => n.includes('##') || n.includes('bb')).length;

export const getProperScaleSpelling = (rootNote, scaleIntervals) => {
  const rootIndex = resolveAllNotesIndex(rootNote);
  if (rootIndex === -1) {
    console.error(`Root note ${rootNote} not found`);
    return [];
  }

  // Candidate root spellings: the requested one first, then its single-accidental
  // enharmonics (e.g. D# -> Eb). We keep the spelling that yields the fewest
  // double accidentals, so an awkward root like D# Dorian is rendered as Eb.
  const basicRoot = ALL_NOTES[rootIndex];
  const candidates = [rootNote];
  (ENHARMONIC_NOTES[basicRoot] || []).forEach((cand) => {
    if (!cand.includes('##') && !cand.includes('bb') && !candidates.includes(cand)) {
      candidates.push(cand);
    }
  });

  let best = null;
  let bestDoubles = Infinity;
  for (const candidate of candidates) {
    const spelled = spellScaleFromRoot(candidate, scaleIntervals);
    const doubles = countDoubleAccidentals(spelled);
    if (doubles < bestDoubles) {
      bestDoubles = doubles;
      best = spelled;
    }
    if (doubles === 0) break; // requested spelling is already clean, keep it
  }

  return best || [];
};

// Spell a scale from a candidate tonic and tally how messy it reads: how many
// notes need a double sharp/flat (## / bb, which look like the app is broken)
// and how many accidentals overall.
const spellingProfile = (rootNote, scaleIntervals) => {
  const notes = getProperScaleSpelling(rootNote, scaleIntervals);
  let doubles = 0;
  let total = 0;
  notes.forEach((note) => {
    const accidentals = (note.match(/[#b]/g) || []).length;
    total += accidentals;
    if (accidentals > 1) doubles += 1;
  });
  return { doubles, total };
};

/**
 * Pick the tonic spelling (sharp vs flat enharmonic) that spells the scale most
 * cleanly. A "black key" tonic such as D# is relabelled Eb when that avoids
 * spellings like F## (D# Ionian -> Eb Ionian), while a mode whose flat spelling
 * would need a double flat (Eb Locrian -> Bbb) keeps the sharp tonic.
 *
 * Selection order, matching conventional notation:
 *   1. fewest double accidentals (## / bb) - these are what read as "broken"
 *   2. fewest accidentals overall
 *   3. on a full tie: prefer the sharp tonic when the scale needs a double (one
 *      double sharp like F## reads cleaner than a double flat like Bbb, e.g.
 *      C# Hungarian Minor), otherwise prefer the flat tonic (Gb over F#).
 *
 * Internal note keys stay in ALL_NOTES (sharps); only the display spelling
 * changes, so saved patterns and pitch comparisons are unaffected.
 * @param {string} rootNote - The root note (always a sharp/natural from ALL_NOTES)
 * @param {number[]} scaleIntervals - Array of semitone intervals from the root
 * @returns {string} - The preferred enharmonic spelling of the root
 */
export const getBestRootSpelling = (rootNote, scaleIntervals) => {
  const flatRoot = ENHARMONIC_EQUIVALENTS[rootNote];
  if (!flatRoot) return rootNote; // white key, nothing to choose

  const sharp = spellingProfile(rootNote, scaleIntervals);
  const flat = spellingProfile(flatRoot, scaleIntervals);

  if (flat.doubles !== sharp.doubles) return flat.doubles < sharp.doubles ? flatRoot : rootNote;
  if (flat.total !== sharp.total) return flat.total < sharp.total ? flatRoot : rootNote;
  // Full tie: a needed double reads cleaner as a sharp tonic; otherwise flats win.
  return sharp.doubles > 0 ? rootNote : flatRoot;
};

/**
 * Get pentatonic scale notes with appropriate enharmonic spelling
 * @param {string} rootNote - The root note of the scale
 * @param {string} mode - The mode name
 * @param {number[]} intervals - Array of semitone intervals from the root
 * @returns {string[]} - Array of properly spelled note names
 */
export const getPentatonicScaleNotes = (rootNote, mode, intervals) => {
  // Resolve root index with full enharmonic support
  let rootIndex = resolveAllNotesIndex(rootNote);
  
  if (rootIndex === -1) {
    console.error(`Root note ${rootNote} not found`);
    return [];
  }
  
  // Special handling for Japanese scales - always use flats
  if (['Hirajoshi', 'Kumoi', 'In Sen', 'Iwato', 'Hon-Kumoi-Joshi'].includes(mode)) {
    return intervals.map(interval => {
      const noteIndex = (rootIndex + interval) % 12;
      const note = ALL_NOTES[noteIndex];
      // Use enharmonic equivalents for Japanese scales
      if (note === 'C#') return 'Db';
      if (note === 'D#') return 'Eb';
      if (note === 'F#') return 'Gb';
      if (note === 'G#') return 'Ab';
      if (note === 'A#') return 'Bb';
      return note;
    });
  }
  
  // Other pentatonic scales follow root note convention. A "black key" tonic
  // (D#, A#, ...) is spelled with flats so it matches the heptatonic display
  // (Eb, Bb, ...) instead of staying on the messy sharp spelling.
  const useFlats = FLAT_KEYS.includes(rootNote) || rootNote.includes('b') || !!ENHARMONIC_EQUIVALENTS[rootNote];
  return intervals.map(interval => {
    const noteIndex = (rootIndex + interval) % 12;
    const note = ALL_NOTES[noteIndex];
    if (useFlats && ENHARMONIC_EQUIVALENTS[note]) {
      return ENHARMONIC_EQUIVALENTS[note];
    }
    return note;
  });
};



// Scale types - auto-generated from MODES
export const SCALE_TYPES = GENERATED_SCALE_TYPES;

// Mood to Scale mapping - auto-generated from moodMappings
export const MOOD_TO_SCALE = GENERATED_MOOD_TO_SCALE;

// Get all available moods - auto-generated
export const ALL_MOODS = GENERATED_ALL_MOODS;

// Re-export MODES for backward compatibility
export { MODES };


// Helper function to get mode intervals (for backward compatibility)
export const getModeIntervals = (modeName) => {
  const mode = MODES[modeName];
  return mode ? (mode.intervals || mode) : null;
};

// Helper function to get default progression for a mode
export const getDefaultProgression = (modeName) => {
  const mode = MODES[modeName];
  if (!mode || !mode.defaultProgressionKey) return null;
  
  // Import the progression from chordProgressions
  const { getProgressionByName } = require('./chordProgressions');
  return getProgressionByName(mode.defaultProgressionKey);
};

// Get the display name for a progression
export const getProgressionDisplayName = (progressionName) => {
  return progressionName || '';
};

// Mode names for each scale type - auto-generated from MODES
export const MODE_NAMES_BY_SCALE = GENERATED_MODE_NAMES_BY_SCALE;

// Mode names in order (for backward compatibility) - auto-generated
export const MODE_NAMES = GENERATED_MODE_NAMES;

// Helper functions - re-exported from musicTheory/index
// getScaleTypeForMode, getModesForScaleType, getAvailableScaleTypes, getModeInfo, getMoodsForScale are imported at top
export { getScaleTypeForMode, getModesForScaleType, getAvailableScaleTypes, getModeInfo, getMoodsForScale };

// Tier classification re-exports
export {
  SCALE_TIERS,
  SCALE_TIER_LABELS,
  SCALE_TIER_DESCRIPTIONS,
  MODES_BY_TIER,
  MODE_TO_TIER,
  SCALE_TYPES_BY_TIER,
  getTierForMode,
  getTierForScaleType,
  getModesByTier,
  getModesForScaleTypeInTier,
  SCALE_DIFFICULTY_ORDER,
  SCALE_DIFFICULTY_LABELS,
  getScaleDifficulty
} from './musicTheory/index';


// Pentatonic harmony types - more authentic than forced Western triads
export const PENTATONIC_HARMONY_TYPES = {
  'drone': 'Drone + Root',        // Single sustained note
  'power': 'Power Interval',      // Root + 5th or closest strong interval
  'quartal': 'Quartal Stack',     // Stacked 4ths
  'dyad': 'Open Interval',        // Two-note intervals (4th, 5th, etc.)
  'cluster': 'Note Cluster'       // 3+ adjacent scale tones
};


// Get all available moods from the mapping
export const getAvailableMoods = () => {
  return Object.keys(MOOD_TO_SCALE);
};

// Get scale degrees (I, ii, iii, etc.) based on the number of notes in the scale.
// When chordQualities is provided (one quality per scale degree, e.g. from
// getChordDescriptorsInMode), the numerals reflect the actual chord qualities of the
// current mode: uppercase = major, lowercase = minor, ° = diminished, ø = half-diminished,
// + = augmented. Without qualities, falls back to the major-scale template.
export const getScaleDegrees = (scaleLength, chordQualities = null) => {
  const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

  if (!chordQualities) {
    // Standard scale degrees for diatonic scales (7 notes)
    if (scaleLength === 7) {
      return ['I', 'ii', 'iii', 'IV', 'V', 'vi', 'vii°'];
    }
    return Array(scaleLength).fill().map((_, i) => romanNumerals[i % romanNumerals.length]);
  }

  const degreeForQuality = (numeral, quality) => {
    switch (quality) {
      case 'maj':
      case 'maj7':
      case 'dom7':
        return numeral;
      case 'min':
      case 'min7':
      case 'minMaj7':
        return numeral.toLowerCase();
      case 'dim':
      case 'dim7':
        return numeral.toLowerCase() + '°';
      case 'half_dim7':
        return numeral.toLowerCase() + 'ø';
      case 'aug':
      case 'augMaj7':
        return numeral + '+';
      default: {
        // Honest display symbols (from honestSymbolFromNotes / ARPEGGIO_TYPES ids)
        // carry their alterations into the numeral so it stops reading as plain
        // major. Pentatonic types (drone/power/quartal...) keep plain numerals.
        if (!quality || typeof quality !== 'string') return numeral;
        const base = /^m(?!aj)/.test(quality) ? numeral.toLowerCase() : numeral;
        let suffix = '';
        if (quality.includes('sus')) suffix += 'sus';
        if (quality.includes('♭5')) suffix += '♭5';
        else if (quality.includes('♯5')) suffix += '+';
        return base + suffix;
      }
    }
  };

  return Array(scaleLength).fill().map((_, i) => {
    const numeral = romanNumerals[i % romanNumerals.length];
    return degreeForQuality(numeral, chordQualities[i]);
  });
};

// Scale-position Roman numerals for a mode (root-first), reflecting the actual
// chord qualities of each degree. Same convention as the chord pads.
export const getModeDegreeNumerals = (rootNote, mode, chordExtension = '7') => {
  const descriptors = getChordDescriptorsInMode(rootNote, mode, chordExtension);
  if (!Array.isArray(descriptors) || descriptors.length === 0) return [];
  const notes = descriptors.map(d => d.note);
  const qualities = descriptors.map(d => d.displayType || d.type);
  const rootIndex = notes.indexOf(rootNote);
  const orderedQualities = rootIndex > 0
    ? [...qualities.slice(rootIndex), ...qualities.slice(0, rootIndex)]
    : qualities;
  return getScaleDegrees(orderedQualities.length, orderedQualities);
};

// Roman numeral string for a progression in a given mode, derived from the real
// chord qualities so the progression label always matches the pads (e.g. Aeolian
// shows i-VII-VI-VII). Falls back to the parenthetical baked into the name.
export const getProgressionNumeralsInMode = (progressionName, rootNote, mode, chordExtension = '7') => {
  if (!progressionName) return '';
  const progression = getProgressionByName(progressionName);
  const degrees = getModeDegreeNumerals(rootNote, mode, chordExtension);
  if (progression && Array.isArray(progression.degrees) && degrees.length > 0) {
    const numerals = progression.degrees.map(d => degrees[degreeToIndex(d, degrees.length)]);
    if (numerals.every(Boolean)) return numerals.join('-');
  }
  const match = progressionName.match(/\(([^)]+)\)/);
  return match ? match[1] : progressionName;
};

// Strip the parenthetical from a progression key to get its plain display name.
export const getProgressionBaseName = (progressionName) =>
  (progressionName || '').replace(/\s*\([^)]+\)/, '').trim();

// Get notes in a specific mode
export const getNotesInMode = (rootNote, mode) => {
  const modeData = MODES[mode];
  
  if (!modeData) {
    console.warn(`Mode "${mode}" not found`);
    return [];
  }
  
  // Extract intervals from the mode data (handle both old array format and new object format)
  const modeIntervals = modeData.intervals || modeData;
  
  // Use the new spelling function for heptatonic (7-note) scales. Pick the tonic
  // spelling that avoids double sharps/flats first (e.g. D# Ionian -> Eb Ionian).
  if (modeIntervals.length === 7) {
    const spelledRoot = getBestRootSpelling(rootNote, modeIntervals);
    return getProperScaleSpelling(spelledRoot, modeIntervals);
  }
  
  // For pentatonic and other scales, use specific logic
  return getPentatonicScaleNotes(rootNote, mode, modeIntervals);
};

/**
 * Find the seven-note (or larger) parent scales a pentatonic fits inside, at the
 * same root. A parent qualifies when its pitch-class set contains every note of
 * the pentatonic. This is the "superimposition" view: e.g. A minor pentatonic
 * fits A Aeolian, A Dorian and A Phrygian; A major pentatonic fits A Ionian,
 * A Lydian and A Mixolydian.
 * @param {string} rootNote - The pentatonic's root note
 * @param {string} mode - The current (pentatonic) mode name
 * @returns {Array<{mode: string, root: string, displayName: string}>}
 */
export const getPentatonicParentModes = (rootNote, mode) => {
  const modeData = MODES[mode];
  if (!modeData || !modeData.isPentatonic) return [];

  const pentSet = scaleIndexSet(getNotesInMode(rootNote, mode));
  if (pentSet.size === 0) return [];

  const rootIdx = resolveAllNotesIndex(rootNote);
  const parents = [];

  for (const [modeName, data] of Object.entries(MODES)) {
    if (data.isPentatonic) continue;
    const intervals = data.intervals || data;
    if (!Array.isArray(intervals) || intervals.length < 7) continue;

    const parentSet = new Set(intervals.map(iv => (rootIdx + iv) % 12));
    let isSuperset = true;
    for (const pc of pentSet) {
      if (!parentSet.has(pc)) { isSuperset = false; break; }
    }
    if (isSuperset) {
      parents.push({ mode: modeName, root: rootNote, displayName: data.displayName || modeName });
    }
  }

  return parents;
};

/**
 * Get the contextual spelling of the root note for display (e.g. "Eb" instead
 * of "D#" when the current mode spells the scale with flats). This is just the
 * first note of the properly spelled scale; the internal rootNote key is
 * unchanged, so saved patterns are unaffected.
 * @param {string} rootNote - The internal root note (always a sharp/natural from ALL_NOTES)
 * @param {string} mode - The current mode name
 * @returns {string} - The root note spelled for display
 */
export const getDisplayRootNote = (rootNote, mode) => {
  const notes = getNotesInMode(rootNote, mode);
  return (notes && notes[0]) || rootNote;
};

// Plain-language label for the two modes every guitarist knows by another name:
// "Major (Ionian)" / "Minor (Aeolian)". Other modes keep their name as-is.
const MODE_PLAIN_NAMES = { Ionian: 'Major', Aeolian: 'Minor' };
export const getModeDisplayLabel = (mode) =>
  MODE_PLAIN_NAMES[mode] ? `${MODE_PLAIN_NAMES[mode]} (${mode})` : mode;

// Three-note triad shapes (intervals from the chord root) used to name a
// pentatonic voicing in any inversion.
const PENTA_TRIAD_SHAPES = [
  { intervals: [0, 4, 7], suffix: '' },     // major
  { intervals: [0, 3, 7], suffix: 'm' },    // minor
  { intervals: [0, 2, 7], suffix: 'sus2' },
  { intervals: [0, 5, 7], suffix: 'sus4' },
  { intervals: [0, 3, 6], suffix: 'dim' },
  { intervals: [0, 4, 8], suffix: 'aug' }
];

// Name a three-note voicing by finding which of its notes turns the set into a
// recognised triad, then labelling it with a slash bass when that root isn't the
// lowest note (e.g. E-G-C -> C/E).
const namePentaVoicing = (chordNotes) => {
  const bassPc = resolveAllNotesIndex(chordNotes[0]);
  const pcs = [...new Set(chordNotes.map(resolveAllNotesIndex))];
  for (const rootPc of pcs) {
    const rel = pcs.map(pc => (pc - rootPc + 12) % 12).sort((a, b) => a - b);
    const shape = PENTA_TRIAD_SHAPES.find(s =>
      s.intervals.length === rel.length && s.intervals.every((v, i) => v === rel[i])
    );
    if (shape) {
      return {
        // Nom epele par la gamme (chordNotes), jamais ALL_NOTES : les gammes
        // japonaises sont en bemols et ALL_NOTES ne connait que les dieses.
        rootName: chordNotes.find(n => resolveAllNotesIndex(n) === rootPc),
        suffix: shape.suffix,
        quality: shape.suffix === '' ? 'maj' : shape.suffix === 'm' ? 'min' : shape.suffix,
        isInversion: rootPc !== bassPc
      };
    }
  }
  return null;
};

// Three-note readings for the trichords no triad shape names: a seventh chord
// missing one note, found from whichever member turns the set into it.
const PENTA_FALLBACK_SHAPES = [
  { intervals: [0, 3, 10], type: 'm7no5', suffix: 'm7(no5)' },
  { intervals: [0, 4, 11], type: 'maj7no5', suffix: 'maj7(no5)' },
  { intervals: [0, 7, 10], type: 'm7no3', suffix: 'm7(no3)' },
  { intervals: [0, 7, 11], type: 'maj7no3', suffix: 'maj7(no3)' },
  { intervals: [0, 4, 6], type: 'majb5', suffix: '(♭5)' }
];

// Label a trichord that PENTA_TRIAD_SHAPES cannot name. Exact readings first,
// structural tag only when none matches.
const nameFallbackTrichord = (chordNotes) => {
  const bassPc = resolveAllNotesIndex(chordNotes[0]);
  const pcs = [...new Set(chordNotes.map(resolveAllNotesIndex))];
  for (const rootPc of pcs) {
    const rel = pcs.map(pc => (pc - rootPc + 12) % 12).sort((a, b) => a - b);
    const shape = PENTA_FALLBACK_SHAPES.find(s =>
      s.intervals.length === rel.length && s.intervals.every((v, i) => v === rel[i])
    );
    if (!shape) continue;
    const rootName = chordNotes.find(n => resolveAllNotesIndex(n) === rootPc);
    const label = rootPc === bassPc
      ? shape.suffix
      : `${rootName}${shape.suffix}/${chordNotes[0]}`;
    return { type: shape.type, label };
  }
  const hasTritone = pcs.some(a => pcs.some(b => (b - a + 12) % 12 === 6));
  return hasTritone
    ? { type: 'tritone', label: '(tritone)' }
    : { type: 'cluster', label: '(cluster)' };
};

// Per Nilsson's pentatonic voicings: build a three-note chord on each scale
// degree by stacking notes from WITHIN the pentatonic, starting from the triad
// that names the scale (e.g. A minor for A minor pentatonic) and moving every
// note up to the next pentatonic degree. The resulting chords are transpositions
// of the shape inside the scale (same relative positions, different degrees),
// assumed as voicings, not functional harmony.
export const getPentatonicChordTypes = (rootNote, mode) => {
  const notes = getNotesInMode(rootNote, mode);
  const n = notes.length;
  if (n < 3) {
    return notes.map(note => ({ note, type: 'drone', notes: [note], displayName: `${note}` }));
  }

  const rootPc = resolveAllNotesIndex(notes[0]);
  const intervalOf = (i) => (resolveAllNotesIndex(notes[i]) - rootPc + 12) % 12;
  // Locate the tonic triad's third (minor or major) and fifth within the penta.
  let thirdIdx = notes.findIndex((_, i) => i > 0 && (intervalOf(i) === 3 || intervalOf(i) === 4));
  let fifthIdx = notes.findIndex((_, i) => i > 0 && intervalOf(i) === 7);
  // Fallback for exotic pentatonics with no plain third/fifth: stack adjacents.
  if (thirdIdx === -1) thirdIdx = 1;
  if (fifthIdx === -1 || fifthIdx === thirdIdx) fifthIdx = Math.min(thirdIdx + 1, n - 1);
  const shape = [0, thirdIdx, fifthIdx];

  return notes.map((note, degree) => {
    const chordNotes = shape.map(s => notes[(s + degree) % n]);
    const named = namePentaVoicing(chordNotes);
    if (!named) {
      const fallback = nameFallbackTrichord(chordNotes);
      return {
        note,
        type: fallback.type,
        notes: chordNotes,
        displayName: `${note} ${fallback.label}`
      };
    }
    const label = named.isInversion
      ? `${named.rootName}${named.suffix}/${note}`
      : named.suffix;
    return {
      note,
      type: named.quality,
      notes: chordNotes,
      displayName: `${note} ${label}`.trim()
    };
  });
};

/**
 * Build a 7th chord from scale degrees (not chromatic intervals)
 * @param {string} rootNote - The root note of the chord
 * @param {string[]} scaleNotes - Array of notes in the scale
 * @returns {string[]} - Array of four notes forming the 7th chord [root, third, fifth, seventh]
 */
const buildTriadFromScale = (rootNote, scaleNotes) => {
  // Find the position of the root note in the scale
  const rootIndex = scaleNotes.findIndex(note => {
    // Handle enharmonic equivalents
    return resolveAllNotesIndex(note) === resolveAllNotesIndex(rootNote);
  });

  if (rootIndex === -1) {
    console.warn(`Root note ${rootNote} not found in scale`);
    return [rootNote];
  }

  const scaleLength = scaleNotes.length;

  // Build 7th chord by stacking scale degrees: 1, 3, 5, 7
  const root = scaleNotes[rootIndex];
  const third = scaleNotes[(rootIndex + 2) % scaleLength];  // Skip one scale degree
  const fifth = scaleNotes[(rootIndex + 4) % scaleLength];  // Skip two scale degrees from root
  const seventh = scaleNotes[(rootIndex + 6) % scaleLength]; // Skip three scale degrees from root

  return [root, third, fifth, seventh];
};

/**
 * Determine chord quality from the intervals in a chord (supports triads and 7th chords)
 * @param {string[]} chordNotes - Array of notes [root, third, fifth] or [root, third, fifth, seventh]
 * @returns {string} - Chord quality: 'maj7', 'min7', 'dom7', 'half_dim7', 'dim7', 'minMaj7', 'augMaj7', or triad fallbacks
 */
// Signatures exactes (intervalles empiles depuis la basse) que le nommage sait
// reconnaitre. Ces qualites sont aussi des cles internes (audioEngine, arpeges) :
// ne jamais y introduire de nouvelle valeur sans verifier chaque consommateur.
const SEVENTH_QUALITY_BY_SIGNATURE = {
  '4,7,11': 'maj7',
  '3,7,10': 'min7',
  '4,7,10': 'dom7',
  '3,6,10': 'half_dim7',
  '3,6,9': 'dim7',
  '3,7,11': 'minMaj7',
  '4,8,11': 'augMaj7'
};
const TRIAD_QUALITY_BY_SIGNATURE = {
  '3,7': 'min',
  '4,7': 'maj',
  '3,6': 'dim',
  '4,8': 'aug'
};

const stackedIntervalsFromRoot = (chordNotes) => {
  const rootIndex = resolveAllNotesIndex(chordNotes[0]);
  return chordNotes.slice(1).map(note => (resolveAllNotesIndex(note) - rootIndex + 12) % 12);
};

// True when the stack maps to one of the legacy quality keys above; false means
// getChordQualityFromIntervals would lie ('maj' fallback) about this stack.
const isKnownStack = (chordNotes) => {
  if (chordNotes.length < 3) return true;
  const intervals = stackedIntervalsFromRoot(chordNotes);
  if (chordNotes.length >= 4) {
    return Boolean(SEVENTH_QUALITY_BY_SIGNATURE[intervals.slice(0, 3).join(',')]);
  }
  return Boolean(TRIAD_QUALITY_BY_SIGNATURE[intervals.join(',')]);
};

const getChordQualityFromIntervals = (chordNotes) => {
  if (chordNotes.length < 3) {
    return 'maj'; // Default fallback
  }

  const intervals = stackedIntervalsFromRoot(chordNotes);
  const [thirdInterval, fifthInterval, seventhInterval] = intervals;

  // If we have a 7th note, detect 7th chord quality
  if (chordNotes.length >= 4) {
    const seventhQuality = SEVENTH_QUALITY_BY_SIGNATURE[intervals.slice(0, 3).join(',')];
    if (seventhQuality) return seventhQuality;

    // Fallback for unusual 7th chord intervals
    console.warn(`Unusual 7th chord intervals: 3rd=${thirdInterval}, 5th=${fifthInterval}, 7th=${seventhInterval} for ${chordNotes.slice(0, 4).join('-')}`);
  }

  // Triad fallback (for 3-note chords or unrecognized 7th intervals)
  const triadQuality = TRIAD_QUALITY_BY_SIGNATURE[`${thirdInterval},${fifthInterval}`];
  if (triadQuality) return triadQuality;

  console.warn(`Unusual chord intervals: 3rd=${thirdInterval}, 5th=${fifthInterval} for ${chordNotes.slice(0, 3).join('-')}`);
  return 'maj';
};

// Display-only honest name for a stack the legacy table can't recognize. Never
// answers 'maj' by default. Two layers: exact pitch-class match against the
// ARPEGGIO_TYPES reference (sus2, min6, quartal...), then a name built from the
// intervals themselves ('(♭5)', 'sus2(♭5)', '7(♭5)'...). The result feeds
// displayName/displayType only; the legacy quality keeps driving the audio.
const HONEST_ADD_LABELS = { 1: 'add♭9', 2: 'add9', 5: 'add11', 9: 'add13' };

const honestSymbolFromNotes = (chordNotes) => {
  const rootIndex = resolveAllNotesIndex(chordNotes[0]);
  const pcs = new Set();
  chordNotes.slice(1).forEach(note => {
    const interval = (resolveAllNotesIndex(note) - rootIndex + 12) % 12;
    if (interval !== 0) pcs.add(interval);
  });

  const stackSet = new Set([0, ...pcs]);
  const arpeggio = getAllArpeggioTypes().find(a => {
    const refSet = new Set(a.intervals.map(interval => interval % 12));
    return refSet.size === stackSet.size && [...refSet].every(interval => stackSet.has(interval));
  });
  if (arpeggio) return arpeggio.id;

  const used = new Set();
  const take = (interval) => { used.add(interval); return true; };
  const has = (interval) => pcs.has(interval);

  const third = has(4) && take(4) ? '' : (has(3) && take(3) ? 'm' : null);
  const seventh = has(11) && take(11) ? 'maj7'
    : (has(10) && take(10) ? '7'
      : (has(9) && take(9) ? '6' : ''));
  const sus = third === null
    ? (has(5) && take(5) ? 'sus4' : (has(2) && take(2) ? 'sus2' : ''))
    : '';

  const alterations = [];
  if (has(7)) take(7);
  else if (has(6) && take(6)) alterations.push('♭5');
  else if (has(8) && take(8)) alterations.push('♯5');
  else alterations.push('no5');
  [...pcs].filter(interval => !used.has(interval)).sort((a, b) => a - b).forEach(interval => {
    alterations.push(HONEST_ADD_LABELS[interval] || `add${interval}`);
  });

  const alt = alterations.length ? `(${alterations.join(',')})` : '';
  return `${third || ''}${seventh}${sus}${alt}`;
};

/**
 * Map a 7th-chord quality to its triad counterpart.
 * Used when chordExtension === '3'.
 */
export const triadQualityFromSeventhQuality = (sevenQuality) => {
  switch (sevenQuality) {
    case 'maj7': return 'maj';
    case 'min7': return 'min';
    case 'dom7': return 'maj';
    case 'half_dim7': return 'dim';
    case 'dim7': return 'dim';
    case 'minMaj7': return 'min';
    case 'augMaj7': return 'aug';
    // Already-triad qualities pass through
    case 'maj':
    case 'min':
    case 'dim':
    case 'aug':
      return sevenQuality;
    default:
      return sevenQuality;
  }
};

/**
 * Format a raw chord quality or interval label into a solfège display symbol.
 * Display-only: internal identifiers (audio engine switch keys, modes' chordQualities,
 * chordProgressions) are never renamed, so the sound never changes. Unknown inputs
 * pass through unchanged.
 * @param {string} quality - Raw quality ('half_dim7', 'min7'...) or interval label ('(5th)'...)
 * @returns {string} - Display symbol ('ø7', 'm7', '5', ...); '' for plain major
 */
export const formatChordQuality = (quality) => {
  if (!quality || typeof quality !== 'string') {
    return '';
  }
  switch (quality.trim()) {
    // 7th chords
    case 'maj7': return 'maj7';
    case 'min7': return 'm7';
    case 'dom7': return '7';
    case 'half_dim7': return 'ø7';
    case 'dim7': return '°7';
    case 'minMaj7': return 'm(maj7)';
    case 'augMaj7': return '+maj7';
    // triads
    case 'maj': return '';
    case 'min': return 'm';
    case 'dim': return '°';
    case 'aug': return '+';
    // ARPEGGIO_TYPES ids surfaced by the honest display naming
    case 'min6': return 'm6';
    case 'maj6': return '6';
    // drone (single sustained note) carries no chord-quality label
    case 'drone': return '';
    // interval labels (power chords / dyads from pentatonic scales)
    case '(5th)': return '5';
    case '(4th)': return '4';
    case '(3rd)': return '3';
    case '(2nd)': return '2';
    case '(quartal)': return '(quartal)';
    // pentatonic trichords no triad shape names (getPentatonicChordTypes)
    case 'm7no5': return 'm7(no5)';
    case 'm7no3': return 'm7(no3)';
    case 'maj7no5': return 'maj7(no5)';
    case 'maj7no3': return 'maj7(no3)';
    case 'majb5': return '(♭5)';
    case 'cluster': return '(cluster)';
    case 'tritone': return '(tritone)';
    default:
      return quality.trim();
  }
};

/**
 * Plain-English name for a chord quality or interval label, for tooltips.
 * Spells out what the terse pad symbol means ('5' -> 'Power chord',
 * 'ø7' source -> 'Half-diminished 7th'). Returns '' when there is nothing
 * worth explaining (plain major triad, drone). Unknown inputs return ''.
 * @param {string} quality - Raw quality ('half_dim7'...) or interval label ('(5th)'...)
 * @returns {string} - Human-readable name, or '' if none
 */
export const chordQualityName = (quality) => {
  if (!quality || typeof quality !== 'string') {
    return '';
  }
  switch (quality.trim()) {
    case 'maj7': return 'Major 7th';
    case 'min7': return 'Minor 7th';
    case 'dom7': return 'Dominant 7th';
    case 'half_dim7': return 'Half-diminished 7th';
    case 'dim7': return 'Diminished 7th';
    case 'minMaj7': return 'Minor-major 7th';
    case 'augMaj7': return 'Augmented major 7th';
    case 'min': return 'Minor';
    case 'dim': return 'Diminished';
    case 'aug': return 'Augmented';
    // interval labels (power chords / dyads from pentatonic scales)
    case '(5th)': return 'Power chord';
    case '(4th)': return 'Perfect 4th';
    case '(3rd)': return 'Third';
    case '(2nd)': return 'Second';
    case '(quartal)': return 'Quartal';
    // pentatonic trichords no triad shape names (getPentatonicChordTypes)
    case 'm7no5': return 'Minor 7th, no 5th';
    case 'm7no3': return 'Minor 7th, no 3rd';
    case 'maj7no5': return 'Major 7th, no 5th';
    case 'maj7no3': return 'Major 7th, no 3rd';
    case 'majb5': return 'Major, flat 5th';
    case 'cluster': return 'Note cluster';
    case 'tritone': return 'Tritone trichord';
    // plain major triad and drone need no gloss
    case 'maj':
    case 'drone':
      return '';
    default:
      return '';
  }
};

// Sentinel "progression" name for the infinite random-chord mode. Not a real
// progression (no degrees); the player generates chords on the fly instead.
export const RANDOM_PROGRESSION = '__random__';

// Get chord names for a specific mode starting from a root note
// chordExtension: '7' (default, 4-note 7th chords) or '3' (3-note triads)
export const getChordDescriptorsInMode = (rootNote, mode, chordExtension = '7') => {
  const notes = getNotesInMode(rootNote, mode);

  if (!Array.isArray(notes) || notes.length === 0) {
    return [];
  }

  const modeData = MODES[mode];

  // Drone voicing: every pad is just its own note (sustained over the scale-root
  // drone bass). Works on any scale type, pentatonics included.
  if (chordExtension === '1') {
    return notes.map(note => ({
      note,
      type: 'drone',
      notes: [note],
      displayName: `${note}`,
      harsh: false
    }));
  }

  // Handle pentatonic scales specially (they don't follow traditional triadic harmony)
  if (modeData && modeData.isPentatonic) {
    const pentatonicChords = getPentatonicChordTypes(rootNote, mode);
    return notes.map(note => {
      const chord = pentatonicChords.find(entry => entry.note === note);
      if (chord) {
        return { ...chord, harsh: false };
      }
      return {
        note,
        type: 'drone',
        notes: [note],
        displayName: `${note}`,
        harsh: false
      };
    });
  }

  // For heptatonic (7-note) and other diatonic scales, build chords from scale degrees
  return notes.map((note) => {
    // Build full 7th chord by stacking scale degrees (4 notes)
    const seventhNotes = buildTriadFromScale(note, notes);

    const chordNotes = chordExtension === '3' ? seventhNotes.slice(0, 3) : seventhNotes;
    const type = getChordQualityFromIntervals(chordNotes);
    const harsh = isHarshChordFromNotes(chordNotes, resolveAllNotesIndex);

    // The displayed name must match the stacked notes. When the legacy table
    // can't name the stack, `type` lies ('maj' fallback) but keeps driving the
    // audio unchanged; only displayType/displayName become honest. Blues keeps
    // its legacy names on purpose: its pads deliberately SOUND like the genre's
    // outside harmony (see SCALES_HARMONIZED_FROM_OUTSIDE), so there the name
    // follows the sound, not the stack.
    const honest = !SCALES_HARMONIZED_FROM_OUTSIDE.has(mode) && !isKnownStack(chordNotes)
      ? honestSymbolFromNotes(chordNotes)
      : null;
    const displayType = honest || type;

    return {
      note,
      type,
      displayType,
      notes: chordNotes,
      displayName: `${note} ${displayType}`,
      harsh
    };
  });
};

export const getChordNamesInMode = (rootNote, mode) => {
  return getChordDescriptorsInMode(rootNote, mode).map(chord => chord.displayName);
};

// Triad qualities, used to recover the extension the player picked from the
// chord name alone (the audio engine only receives a quality, not the setting).
const TRIAD_QUALITIES = new Set(['maj', 'min', 'dim', 'aug']);

// Scales whose harmony deliberately comes from OUTSIDE themselves, so stacking
// within the scale is the wrong answer. The blues scale is melodic, not
// harmonic: stacking it yields C-F-G, F-G-C, G-C-F, quartal chords with neither
// third nor seventh. The genre wants I7-IV7-V7, which no diatonic scale can
// produce, and the clash between the scale's minor third and the chord's major
// third IS the blues sound. Returning null here keeps the scale on the older
// path, which gives it back its major triads.
const SCALES_HARMONIZED_FROM_OUTSIDE = new Set(['Blues']);

// The exact notes of the voicing whose bass is `chordRoot`, stacked from within
// the scale. The audio engine uses this so a played chord matches the voicing
// shown on the pad instead of rebuilding a generic chord from its name.
//
// Naming a stack and then rebuilding it from that name is not a closed loop.
// getChordQualityFromIntervals falls back to 'maj' on anything it can't name
// (C-E-F#-A# on Oriental), and getChordNotes then plays C-E-G, a note the scale
// doesn't contain: the ear hears what the fretboard doesn't show. Handing over
// the stack itself removes that round trip. On the scales where the loop already
// closed, this returns the same notes as before.
export const getScaleVoicingNotes = (scaleRoot, mode, chordRoot, quality) => {
  if (!scaleRoot || !MODES[mode] || quality === 'drone') return null;
  if (SCALES_HARMONIZED_FROM_OUTSIDE.has(mode)) return null;
  const extension = TRIAD_QUALITIES.has(quality) ? '3' : '7';
  const target = resolveAllNotesIndex(chordRoot);
  const match = getChordDescriptorsInMode(scaleRoot, mode, extension).find(
    v => resolveAllNotesIndex(v.note) === target
  );
  return match ? match.notes : null;
};

// Pentatonic and blues scales don't harmonize on their own; their chords come
// from a parent heptatonic scale. The chord pads borrow that parent's 7 diatonic
// chords, while the neck still shows the 5/6-note scale plus the parent's missing
// notes as dimmed "ghosts". Returns null for ordinary heptatonic scales.
export const PARENT_MODE_FOR_SCALE = {
  'Major Pentatonic': 'Ionian',
  'Minor Pentatonic': 'Aeolian',
  'Blues': 'Aeolian'
};

export const getParentMode = (mode) => PARENT_MODE_FOR_SCALE[mode] || null;

// Notes in the parent scale but absent from the pentatonic/blues scale
// (e.g. C Major Pentatonic -> F and B from C Ionian). Empty for ordinary scales.
export const getGhostNotes = (rootNote, mode) => {
  const parentMode = getParentMode(mode);
  if (!parentMode) return [];
  const scaleSet = new Set(getNotesInMode(rootNote, mode).map(resolveAllNotesIndex));
  return getNotesInMode(rootNote, parentMode)
    .filter(note => !scaleSet.has(resolveAllNotesIndex(note)));
};

// Pitch classes of the chord that is sounding for the active chord, used to drive
// the neck's visual hierarchy: notes in the voicing read as strong (target notes),
// everything else falls back to dim "passage" styling. A bare drone and a plain
// triad imply the same tonic triad, so the triad (1/3/5) is always strong; only a
// 7th-chord voicing promotes the 7th. This is what makes the maj7 read as passage
// over a triad and become a target once you switch to a 7th chord. Returns
// pitch-class indices (0-11). The scale root stays the green anchor separately.
export const getVoicingTonePcs = (rootNote, mode, chordNote, chordExtension) => {
  const effectiveMode = getParentMode(mode) || mode;
  const target = chordNote || rootNote;
  const findChord = (ext) =>
    getChordDescriptorsInMode(rootNote, effectiveMode, ext)
      .find(d => resolveAllNotesIndex(d.note) === resolveAllNotesIndex(target));

  // Triad is always strong (a drone implies the tonic triad).
  const triad = findChord('3');
  if (!triad || !Array.isArray(triad.notes)) {
    // Chord root foreign to the displayed scale (e.g. a locked jam scale that
    // lacks the section's chord root): no diatonic chord exists on it, so fall
    // back to the chord tones (min/maj third, fifth, plus 7ths for a 7th
    // voicing) that DO live in the scale — those are the safe targets to jam.
    const targetPc = resolveAllNotesIndex(target);
    if (targetPc === -1) return [];
    const scalePcs = scaleIndexSet(getNotesInMode(rootNote, effectiveMode));
    const candidates = [3, 4, 7, ...(chordExtension === '7' ? [10, 11] : [])];
    return candidates
      .map(iv => (targetPc + iv) % 12)
      .filter(pc => scalePcs.has(pc));
  }
  const pcs = new Set(triad.notes.map(resolveAllNotesIndex));

  // The 7th only joins the strong set for a 7th-chord voicing.
  if (chordExtension === '7') {
    const seventh = findChord('7');
    if (seventh && Array.isArray(seventh.notes)) {
      seventh.notes.forEach(n => pcs.add(resolveAllNotesIndex(n)));
    }
  }

  pcs.delete(-1);
  return [...pcs];
};

// Chord degrees the neck can follow while a progression runs. Voice leading is
// about a function, not a fixed pitch: the 3rd of Dm is not the 3rd of G7, so
// the anchor has to be recomputed on every chord.
export const TRACKED_DEGREES = [1, 3, 5, 7];

// Pitch class of one degree (1/3/5/7) of the chord that is currently sounding.
// Feeds the neck's green anchor: with degree 1 it lands on the chord root, which
// is exactly what the neck showed before this existed. Returns -1 when the degree
// cannot be resolved, and callers fall back to the scale root rather than losing
// the anchor entirely.
export const getChordDegreePc = (rootNote, mode, chordNote, degree) => {
  const degreeIndex = TRACKED_DEGREES.indexOf(degree);
  if (degreeIndex === -1) return -1;

  const effectiveMode = getParentMode(mode) || mode;
  const target = chordNote || rootNote;
  const targetPc = resolveAllNotesIndex(target);
  if (targetPc === -1) return -1;
  if (degreeIndex === 0) return targetPc;

  // Stacked-thirds spelling of the chord, ordered [1, 3, 5, 7] by
  // buildTriadFromScale, so the degree index reads straight off the array.
  const descriptor = getChordDescriptorsInMode(rootNote, effectiveMode, '7')
    .find(d => resolveAllNotesIndex(d.note) === resolveAllNotesIndex(target));
  const spelled = descriptor && Array.isArray(descriptor.notes)
    ? resolveAllNotesIndex(descriptor.notes[degreeIndex])
    : -1;
  if (spelled !== -1) return spelled;

  // No diatonic chord on that root (chord root foreign to the displayed scale,
  // pentatonic pads, drone voicing): keep the degree that DOES live in the
  // scale, same fallback shape as getVoicingTonePcs.
  const scalePcs = scaleIndexSet(getNotesInMode(rootNote, effectiveMode));
  const intervals = degree === 3 ? [3, 4] : degree === 5 ? [7, 6, 8] : [10, 11];
  const match = intervals
    .map(iv => (targetPc + iv) % 12)
    .find(pc => scalePcs.has(pc));
  return match === undefined ? -1 : match;
};

// Get pentatonic chord notes based on the chord type
export const getPentatonicChordNotes = (rootNote, chordData) => {
  if (chordData && chordData.notes) {
    return chordData.notes;
  }
  return [rootNote]; // Fallback to single note
};

// ---------------------------------------------------------------------------
// Chord voicing (Per Nilsson register model)
// ---------------------------------------------------------------------------
// Every chord is voiced as low as possible inside a fixed one-octave window
// anchored on an absolute floor note, the same in every key. Because the window
// never moves, voicings stay close from one chord to the next (tight voice
// leading) and the chord root is free to land anywhere in the voicing instead of
// always sitting in the bass. The root function is carried by a separate drone
// bass an octave below, so chords no longer sound stuck in first inversion.

// Absolute floor of the voicing window: G3..F#4 keeps triads/7ths bright and
// out of the muddy low register.
export const VOICING_FLOOR_NOTE = 'G';
export const VOICING_FLOOR_OCTAVE = 3;
// The drone bass sits in its own register below the window, always the root,
// never inverted.
export const VOICING_BASS_OCTAVE = 2;
// Highest octave we have samples for; clamps the window so we never request a
// note that cannot be loaded.
const VOICING_OCTAVE_CEILING = 4;

// Lowest octave that places a pitch class at or above the window floor.
const octaveInWindow = (note) => {
  const floorAbs = VOICING_FLOOR_OCTAVE * 12 + resolveAllNotesIndex(VOICING_FLOOR_NOTE);
  const pc = resolveAllNotesIndex(note);
  let octave = VOICING_FLOOR_OCTAVE;
  while (octave * 12 + pc < floorAbs) octave++;
  return Math.min(octave, VOICING_OCTAVE_CEILING);
};

// Degree roles by position in a chord-tone array (getChordNotes order: 1-3-5-7).
const VOICING_ROLES = ['root', 'third', 'fifth', 'seventh'];

/**
 * Build the ordered note+octave list for a chord: an optional low drone bass
 * plus the chord tones placed in the fixed register window.
 *
 * @param {Array<string>} chordNotes - chord tones, root first (e.g. ['C','E','G'])
 * @param {string} rootNote - chord root (used as the bass when no pedal is given)
 * @param {string} [bassNote] - note for the drone bass (scale tonic for a pedal
 *   point, otherwise the chord root). Falls back to rootNote.
 * @param {Object} [options]
 * @param {boolean} [options.drone=true] - include the low drone bass
 * @returns {Array<{key: string, role: string}>} e.g. [{key:'C2',role:'drone'}, ...]
 */
// Sample buffers are keyed with sharp spellings (A#2, not Bb2): normalize so a
// flat-spelled scale never asks the player for a sample file that doesn't exist.
const sampleKey = (note, octave) => {
  const pc = resolveAllNotesIndex(note);
  return `${pc === -1 ? note : ALL_NOTES[pc]}${octave}`;
};

export const voiceChord = (chordNotes, rootNote, bassNote = null, { drone = true } = {}) => {
  const result = [];

  if (drone) {
    const bass = bassNote || rootNote;
    result.push({ key: sampleKey(bass, VOICING_BASS_OCTAVE), role: 'drone' });
  }

  const tones = Array.isArray(chordNotes) ? chordNotes : [];
  tones.forEach((note, i) => {
    result.push({ key: sampleKey(note, octaveInWindow(note)), role: VOICING_ROLES[i] || 'root' });
  });

  return result;
};

// Get chord notes for a specific chord type
export const getChordNotes = (rootNote, quality, mode = null) => {
  const rootIndex = resolveAllNotesIndex(rootNote);
  
  // If this is a pentatonic scale, use the pentatonic chord approach
  if (mode && MODES[mode] && MODES[mode].isPentatonic) {
    const pentatonicChords = getPentatonicChordTypes(rootNote, mode);
    const chordForNote = pentatonicChords.find(chord => resolveAllNotesIndex(chord.note) === rootIndex);
    if (chordForNote) {
      return chordForNote.notes;
    }
  }
  
  // First, try to find the chord type in ARPEGGIO_TYPES
  const arpeggioType = getArpeggioTypeById(quality);
  if (arpeggioType && arpeggioType.intervals) {
    // Use the intervals from the arpeggio type to generate notes
    return arpeggioType.intervals.map(interval => 
      ALL_NOTES[(rootIndex + interval) % 12]
    );
  }
  
  // Fallback to basic chord types for backwards compatibility
  switch(quality) {
    case 'maj':
      return [
        ALL_NOTES[rootIndex],
        ALL_NOTES[(rootIndex + 4) % 12], // Major third
        ALL_NOTES[(rootIndex + 7) % 12]  // Perfect fifth
      ];
    case 'min':
      return [
        ALL_NOTES[rootIndex],
        ALL_NOTES[(rootIndex + 3) % 12], // Minor third
        ALL_NOTES[(rootIndex + 7) % 12]  // Perfect fifth
      ];
    case 'dim':
      return [
        ALL_NOTES[rootIndex],
        ALL_NOTES[(rootIndex + 3) % 12], // Minor third
        ALL_NOTES[(rootIndex + 6) % 12]  // Diminished fifth
      ];
    case 'aug':
      return [
        ALL_NOTES[rootIndex],
        ALL_NOTES[(rootIndex + 4) % 12], // Major third
        ALL_NOTES[(rootIndex + 8) % 12]  // Augmented fifth
      ];
    case '7':
    case 'dom7':
      return [
        ALL_NOTES[rootIndex],
        ALL_NOTES[(rootIndex + 4) % 12], // Major third
        ALL_NOTES[(rootIndex + 7) % 12], // Perfect fifth
        ALL_NOTES[(rootIndex + 10) % 12] // Minor seventh
      ];
    case 'm7':
    case 'min7':
      return [
        ALL_NOTES[rootIndex],
        ALL_NOTES[(rootIndex + 3) % 12], // Minor third
        ALL_NOTES[(rootIndex + 7) % 12], // Perfect fifth
        ALL_NOTES[(rootIndex + 10) % 12] // Minor seventh
      ];
    case 'maj7':
      return [
        ALL_NOTES[rootIndex],
        ALL_NOTES[(rootIndex + 4) % 12], // Major third
        ALL_NOTES[(rootIndex + 7) % 12], // Perfect fifth
        ALL_NOTES[(rootIndex + 11) % 12] // Major seventh
      ];
    default:
      return [ALL_NOTES[rootIndex]];
  }
};

// Re-export chord types for backward compatibility
export { ARPEGGIO_TYPES, getAllArpeggioTypes, getArpeggioTypeById, groupArpeggiosByCategory } from './chordTypes';


// Get all available arpeggios for a given root note in a scale
export const getAvailableArpeggiosInScale = (rootNote, scaleRoot, scaleMode) => {
  // Get all notes in the scale. Compare by pitch class, not spelling: the scale
  // may be spelled with flats (Eb...) while rootNote/arpeggio notes are sharps.
  const scaleNotes = getNotesInMode(scaleRoot, scaleMode);
  const scalePitches = scaleIndexSet(scaleNotes);

  // Check if the root note is in the scale
  if (!scalePitches.has(resolveAllNotesIndex(rootNote))) {
    return []; // Root note not in scale, no arpeggios available
  }

  const rootIndex = resolveAllNotesIndex(rootNote);
  const allArpeggioTypes = getAllArpeggioTypes();
  const availableArpeggios = [];
  
  // For each arpeggio type, check if all its notes are in the scale
  for (const arpeggioType of allArpeggioTypes) {
    // Get the notes of this arpeggio based on intervals
    const arpeggioNotes = arpeggioType.intervals.map(interval => {
      return ALL_NOTES[(rootIndex + interval) % 12];
    });
    
    // Check if all arpeggio notes are in the scale (compare by pitch class)
    const allNotesInScale = arpeggioNotes.every(note => scalePitches.has(resolveAllNotesIndex(note)));
    
    // If all notes are in the scale, this arpeggio is available
    if (allNotesInScale) {
      // Create a map from interval to scale degree (1, 3, 5, etc.)
      const intervalToDegree = {
        0: '1',  // Root
        2: '2',  // Second (for sus2)
        3: 'b3', // Minor third
        4: '3',  // Major third
        5: '4',  // Perfect fourth
        7: '5',  // Perfect fifth
        9: '6',  // Sixth
        10: 'b7', // Minor seventh
        11: '7',  // Major seventh
      };
      
      // Associate each note with its correct scale degree
      const notesWithDegrees = arpeggioType.intervals.map((interval, index) => {
        const note = arpeggioNotes[index];
        const scaleDegree = intervalToDegree[interval] || (index + 1).toString();
        return { note, scaleDegree, interval };
      });
      
      availableArpeggios.push({
        ...arpeggioType,
        root: rootNote,
        notes: arpeggioNotes, // For backwards compatibility
        notesWithDegrees, // New property with notes and their degrees
        name: `${rootNote}${arpeggioType.symbol}`,
        displayName: `${rootNote}${arpeggioType.symbol} : ${arpeggioNotes.join(' - ')}`
      });
    }
  }
  
  return availableArpeggios;
};


// Get chord notes that are constrained to a specific scale/mode
// This function now uses scale-degree stacking instead of chromatic intervals
// NOTE: The 'quality' parameter is kept for backward compatibility but is ignored
// because chord quality is determined by the scale structure itself
export const getScaleChordNotes = (rootNote, quality, scaleRoot, scaleMode) => {
  // Get all notes in the scale/mode
  const scaleNotes = getNotesInMode(scaleRoot, scaleMode);

  // Use buildTriadFromScale to get the correct chord notes
  // This stacks scale degrees (1-3-5) instead of using chromatic intervals
  const triadNotes = buildTriadFromScale(rootNote, scaleNotes);

  return triadNotes;
};
